import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/system';
import { FC, useEffect } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SetUserPasswordUsingPinDto } from 'src/api/userService/dto/SetUserPasswordUsingPin.dto';
import { loginUser } from 'src/auth/auth0';
import { Button } from 'src/components/button/button';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { Section } from 'src/components/section';
import { routes } from 'src/constants/routes';
import { useSetPasswordUsingPin } from 'src/lib/hooks/use-set-password-using-pin';
import { selectQueryData } from 'src/store/slices/dual-occupant-slice';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';

import { createPasswordSchema } from './create-password-schema';
import { styles } from './styles';
import { SetPasswordFormValues } from './types';

type DualOccupantLoginFormProps = {
    isDualOccupantAmend?: boolean;
};

export const DualOccupantLoginForm: FC<DualOccupantLoginFormProps> = ({ isDualOccupantAmend }) => {
    const { t } = useTranslation();
    const queryData = useSelector(selectQueryData);

    const { mutateAsync, isPending } = useSetPasswordUsingPin();

    const methods = useForm<SetPasswordFormValues>({
        resolver: yupResolver(createPasswordSchema()) as Resolver<SetPasswordFormValues>,
        defaultValues: {
            email: '',
            password: '',
            repeatPassword: '',
        },
        mode: 'all',
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { isValid, isSubmitted, isSubmitting },
    } = methods;

    useEffect(() => {
        if (queryData.userEmail) setValue('email', queryData.userEmail);
    }, [queryData.userEmail]);

    const onSubmit = async (data: SetPasswordFormValues) => {
        if (!queryData.vitaRef || !queryData.userEmail || !queryData.auth0Pin) {
            throw new Error(ErrorMessage.DUAL_OCCUPANT_INSUFFICIENT_METADATA);
        }

        const setPasswordData = new SetUserPasswordUsingPinDto({
            email: queryData.userEmail,
            password: data.password,
            pin: queryData.auth0Pin,
        });

        await mutateAsync(setPasswordData, {
            onError: (e) => {
                throw e;
            },
            onSuccess: () => {
                loginUser(
                    queryData.userEmail as string,
                    data.password,
                    `${window.location.origin}${isDualOccupantAmend ? routes.callback.amendBookingDualOccupant : routes.callback.additionalOccupant}`
                );
            },
        });
    };

    const buttonIsLoading = isPending || isSubmitted || isSubmitting;

    return (
        <Section header={t('occupant_set_password')} style={styles.wrapper}>
            <ControlledField
                type="email"
                name="email"
                control={control}
                label={t('your_details_form_email_label')}
                placeholder={t('your_details_form_email_placeholder')}
                disabled
            />
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Password}
                    name="password"
                    control={control}
                    label={t('your_details_form_create_password_label')}
                    placeholder={t('your_details_form_create_password_placeholder')}
                />
                <ControlledField
                    fieldType={FieldTypes.Password}
                    name="repeatPassword"
                    control={control}
                    label={t('your_details_form_repeat_password_label')}
                    placeholder={t('your_details_form_repeat_password_placeholder')}
                />
            </Box>
            <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                css={styles.cta}
                loading={buttonIsLoading}
                disabled={!isValid}
            >
                {t('occupant_set_password_cta')}
            </Button>
        </Section>
    );
};
